import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatApiError } from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { Logo } from "../../components/Logo";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "../../components/ui/dialog";
import { ArrowLeft, UserPlus, KeyRound, UserCheck, UserX, Copy, Check, ShieldCheck, Pencil, Trash2 } from "lucide-react";

function RoleBadge({ role }) {
  if (role === "admin") {
    return (
      <span className="font-mono-label text-[10px] bg-[#4A7A3D] text-[#FAF8F3] px-2 py-1 inline-flex items-center gap-1.5">
        <ShieldCheck size={11} /> ADMIN
      </span>
    );
  }
  return <span className="font-mono-label text-[10px] bg-[#C4944A] text-[#1A1A18] px-2 py-1">ÉDITEUR</span>;
}

function CopyableSecret({ secret, testId }) {
  const [copied, setCopied] = useState(false);
  return (
    <div className="border-2 border-[#C4944A] bg-[#FAF8F3] p-4 flex items-center justify-between gap-3 font-mono">
      <code className="text-lg break-all" data-testid={testId}>{secret}</code>
      <button
        type="button"
        onClick={() => {
          navigator.clipboard?.writeText(secret).catch(() => {});
          setCopied(true);
          setTimeout(() => setCopied(false), 2500);
        }}
        className="min-h-[44px] bg-[#1A1A18] text-[#FAF8F3] px-4 py-2 font-mono-label hover:bg-[#4A7A3D] flex items-center gap-2 flex-shrink-0"
        data-testid={`${testId}-copy`}
      >
        {copied ? <Check size={14} /> : <Copy size={14} />}
        {copied ? "Copié" : "Copier"}
      </button>
    </div>
  );
}

export default function AdminUsers() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteName, setInviteName] = useState("");
  const [inviteRole, setInviteRole] = useState("editor");
  const [inviteSubmitting, setInviteSubmitting] = useState(false);
  const [inviteError, setInviteError] = useState("");
  const [createdUser, setCreatedUser] = useState(null); // {email, temp_password}

  const [resetTarget, setResetTarget] = useState(null);
  const [resetSubmitting, setResetSubmitting] = useState(false);
  const [resetResult, setResetResult] = useState(null);

  const [editTarget, setEditTarget] = useState(null); // {id, name, email, role}
  const [editSubmitting, setEditSubmitting] = useState(false);
  const [editError, setEditError] = useState("");

  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteSubmitting, setDeleteSubmitting] = useState(false);

  useEffect(() => {
    if (user === false) navigate("/admin/login", { replace: true });
    if (user && user.role !== "admin") navigate("/admin", { replace: true });
  }, [user, navigate]);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/users");
      setUsers(data);
      setError("");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "Échec du chargement");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (user?.role === "admin") load(); }, [user]);

  const submitInvite = async (e) => {
    e?.preventDefault();
    setInviteError("");
    setInviteSubmitting(true);
    try {
      const { data } = await api.post("/admin/users", {
        email: inviteEmail.trim().toLowerCase(),
        name: inviteName.trim(),
        role: inviteRole,
      });
      setCreatedUser({ email: data.email, name: data.name, role: data.role, temp_password: data.temp_password });
      setInviteEmail(""); setInviteName(""); setInviteRole("editor");
      setInviteOpen(false);
      load();
    } catch (err) {
      setInviteError(formatApiError(err.response?.data?.detail) || "Échec de l'invitation");
    } finally {
      setInviteSubmitting(false);
    }
  };

  const toggleActive = async (u) => {
    try {
      await api.patch(`/admin/users/${u.id}`, { is_active: !u.is_active });
      load();
    } catch (err) {
      alert(formatApiError(err.response?.data?.detail) || "Échec de la mise à jour");
    }
  };

  const submitResetPwd = async () => {
    if (!resetTarget) return;
    setResetSubmitting(true);
    try {
      const { data } = await api.post(`/admin/users/${resetTarget.id}/reset-password`);
      setResetResult({ email: data.email, temp_password: data.temp_password });
      setResetTarget(null);
    } catch (err) {
      alert(formatApiError(err.response?.data?.detail) || "Échec de la réinitialisation");
    } finally {
      setResetSubmitting(false);
    }
  };

  const submitEdit = async (e) => {
    e?.preventDefault();
    if (!editTarget) return;
    setEditError("");
    setEditSubmitting(true);
    try {
      await api.patch(`/admin/users/${editTarget.id}`, {
        name: editTarget.name.trim(),
        email: editTarget.email.trim().toLowerCase(),
        role: editTarget.role,
      });
      setEditTarget(null);
      load();
    } catch (err) {
      setEditError(formatApiError(err.response?.data?.detail) || "Échec de la modification");
    } finally {
      setEditSubmitting(false);
    }
  };

  const submitDelete = async () => {
    if (!deleteTarget) return;
    setDeleteSubmitting(true);
    try {
      await api.delete(`/admin/users/${deleteTarget.id}`);
      setDeleteTarget(null);
      load();
    } catch (err) {
      alert(formatApiError(err.response?.data?.detail) || "Échec de la suppression");
    } finally {
      setDeleteSubmitting(false);
    }
  };

  if (!user) return <div className="py-32 text-center font-mono-label">CHARGEMENT…</div>;

  return (
    <div className="min-h-screen bg-[#FAF8F3]" data-testid="admin-users-page">
      <header className="border-b-2 border-[#1A1A18] bg-[#FAF8F3]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-5 flex items-center justify-between">
          <div className="flex items-center gap-6">
            <Link to="/admin" className="flex items-center gap-2 text-[#1A1A18] hover:text-[#4A7A3D]" data-testid="users-back">
              <ArrowLeft size={16} /> <Logo size="sm" />
            </Link>
            <span className="font-mono-label text-[#4A7A3D] hidden md:inline">CMS · UTILISATEURS</span>
          </div>
          <button
            type="button"
            onClick={() => setInviteOpen(true)}
            className="min-h-[44px] bg-[#4A7A3D] text-[#FAF8F3] px-4 py-2 font-mono-label hover:bg-[#3E6633] flex items-center gap-2"
            data-testid="users-invite-btn"
          >
            <UserPlus size={14} /> Inviter
          </button>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <h1 className="font-display text-4xl md:text-5xl mb-3">Utilisateurs</h1>
        <p className="text-[#4B4B48] leading-relaxed mb-8 max-w-2xl">
          Invite des collaborateurs (éditeurs) ou d'autres administrateurs.
          Les <strong>éditeurs</strong> peuvent publier des articles, éditer les pages, gérer la boutique et les ressources.
          Les <strong>administrateurs</strong> ont en plus accès aux rubriques, à la gestion des utilisateurs et au mode maintenance.
        </p>

        {error && (
          <div className="border-l-4 border-red-700 bg-red-50 p-4 mb-6 text-sm text-red-900" data-testid="users-error">{error}</div>
        )}

        {loading ? (
          <p className="font-mono-label">CHARGEMENT…</p>
        ) : (
          <div className="border-2 border-[#1A1A18] divide-y divide-[#E0D8CE]">
            {users.map((u) => {
              const isSelf = u.id === user.id;
              return (
                <div
                  key={u.id}
                  className={`p-5 flex flex-col sm:flex-row sm:items-center gap-4 ${u.is_active ? "" : "bg-[#F5EFEB] opacity-70"}`}
                  data-testid={`user-row-${u.email}`}
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 mb-1 flex-wrap">
                      <span className="font-display text-xl text-[#1A1A18]">{u.name || "—"}</span>
                      <RoleBadge role={u.role} />
                      {!u.is_active && (
                        <span className="font-mono-label text-[10px] bg-[#4B4B48] text-[#FAF8F3] px-2 py-1">DÉSACTIVÉ</span>
                      )}
                      {isSelf && (
                        <span className="font-mono-label text-[10px] bg-[#1A1A18] text-[#FAF8F3] px-2 py-1">TOI</span>
                      )}
                    </div>
                    <div className="font-mono-label text-[#4B4B48] break-all">{u.email}</div>
                    <div className="font-mono-label text-[10px] text-[#4B4B48] mt-1">
                      Créé · {u.created_at ? new Date(u.created_at).toLocaleDateString("fr-FR") : "—"}
                      {u.last_login_at && ` · Dernière connexion · ${new Date(u.last_login_at).toLocaleDateString("fr-FR")}`}
                      {!u.password_changed_at && u.email !== user.email && " · ⚠ mot de passe temporaire non changé"}
                    </div>
                  </div>
                  <div className="flex flex-col sm:flex-row gap-2 flex-shrink-0">
                    <button
                      type="button"
                      onClick={() => setEditTarget({ id: u.id, name: u.name || "", email: u.email, role: u.role })}
                      className="min-h-[40px] border-2 border-[#1A1A18] px-3 py-1.5 font-mono-label hover:bg-[#F5EFEB] flex items-center justify-center gap-2"
                      data-testid={`user-${u.email}-edit`}
                    >
                      <Pencil size={14} /> Modifier
                    </button>
                    <button
                      type="button"
                      onClick={() => setResetTarget(u)}
                      disabled={isSelf}
                      className="min-h-[40px] border-2 border-[#1A1A18] px-3 py-1.5 font-mono-label hover:bg-[#F5EFEB] flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                      data-testid={`user-${u.email}-reset`}
                      title={isSelf ? "Utilise /admin/profile pour ton propre mot de passe" : ""}
                    >
                      <KeyRound size={14} /> Réinit. mdp
                    </button>
                    <button
                      type="button"
                      onClick={() => toggleActive(u)}
                      disabled={isSelf}
                      className={`min-h-[40px] px-3 py-1.5 font-mono-label flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed ${
                        u.is_active
                          ? "border-2 border-red-700 text-red-700 hover:bg-red-700 hover:text-[#FAF8F3]"
                          : "border-2 border-[#4A7A3D] text-[#4A7A3D] hover:bg-[#4A7A3D] hover:text-[#FAF8F3]"
                      }`}
                      data-testid={`user-${u.email}-toggle-active`}
                    >
                      {u.is_active ? (<><UserX size={14} /> Désactiver</>) : (<><UserCheck size={14} /> Réactiver</>)}
                    </button>
                    <button
                      type="button"
                      onClick={() => setDeleteTarget(u)}
                      disabled={isSelf}
                      className="min-h-[40px] border-2 border-red-700 bg-red-50 text-red-700 px-3 py-1.5 font-mono-label hover:bg-red-700 hover:text-[#FAF8F3] flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                      data-testid={`user-${u.email}-delete`}
                    >
                      <Trash2 size={14} /> Supprimer
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </main>

      {/* Invite dialog */}
      <Dialog open={inviteOpen} onOpenChange={(o) => !inviteSubmitting && setInviteOpen(o)}>
        <DialogContent data-testid="users-invite-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Inviter un collaborateur</DialogTitle>
            <DialogDescription className="text-[#4B4B48] leading-relaxed pt-2">
              Un mot de passe temporaire fort sera généré. Tu pourras le copier pour le transmettre par WhatsApp ou SMS — il ne sera affiché qu'une seule fois.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={submitInvite} className="space-y-4 pt-2">
            <div>
              <label htmlFor="inv-name" className="font-mono-label text-[#1A1A18] block mb-2">Nom complet</label>
              <input id="inv-name" type="text" required value={inviteName} onChange={(e) => setInviteName(e.target.value)} className="w-full border-2 border-[#1A1A18] bg-[#FAF8F3] px-3 py-3 font-body text-base focus:outline-none focus:border-[#4A7A3D]" data-testid="invite-name" autoComplete="off" />
            </div>
            <div>
              <label htmlFor="inv-email" className="font-mono-label text-[#1A1A18] block mb-2">Email (identifiant de connexion)</label>
              <input id="inv-email" type="email" required value={inviteEmail} onChange={(e) => setInviteEmail(e.target.value)} placeholder="pierre@gmail.com" className="w-full border-2 border-[#1A1A18] bg-[#FAF8F3] px-3 py-3 font-body text-base focus:outline-none focus:border-[#4A7A3D]" data-testid="invite-email" autoComplete="off" />
            </div>
            <div>
              <label htmlFor="inv-role" className="font-mono-label text-[#1A1A18] block mb-2">Rôle</label>
              <select id="inv-role" value={inviteRole} onChange={(e) => setInviteRole(e.target.value)} className="w-full border-2 border-[#1A1A18] bg-[#FAF8F3] px-3 py-3 font-body text-base focus:outline-none focus:border-[#4A7A3D]" data-testid="invite-role">
                <option value="editor">Éditeur (publication articles, pages, boutique, ressources)</option>
                <option value="admin">Administrateur (accès total, incluant utilisateurs)</option>
              </select>
            </div>
            {inviteError && <div className="border-l-4 border-red-700 bg-red-50 p-3 text-sm text-red-900" data-testid="invite-error">{inviteError}</div>}
            <DialogFooter className="flex flex-col sm:flex-row gap-2 pt-2">
              <button type="button" onClick={() => setInviteOpen(false)} disabled={inviteSubmitting} className="min-h-[44px] border-2 border-[#1A1A18] px-5 py-2 font-mono-label hover:bg-[#F5EFEB] disabled:opacity-50">Annuler</button>
              <button type="submit" disabled={inviteSubmitting} className="min-h-[44px] bg-[#4A7A3D] text-[#FAF8F3] px-5 py-2 font-mono-label hover:bg-[#3E6633] disabled:opacity-50" data-testid="invite-submit">
                {inviteSubmitting ? "Création…" : "Créer le compte"}
              </button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Created user reveal */}
      <Dialog open={!!createdUser} onOpenChange={(o) => !o && setCreatedUser(null)}>
        <DialogContent data-testid="users-created-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Compte créé · transmets ce mot de passe maintenant</DialogTitle>
            <DialogDescription className="text-[#4B4B48] leading-relaxed pt-2">
              Le compte de <strong>{createdUser?.name}</strong> (<code className="break-all">{createdUser?.email}</code>) est actif. Ce mot de passe temporaire ne sera <strong>plus jamais affiché</strong>. Copie-le et envoie-le par un canal privé (WhatsApp, SMS). Demande à la personne d'aller sur <code>/admin/profile</code> pour le remplacer dès sa première connexion.
            </DialogDescription>
          </DialogHeader>
          <div className="pt-2">
            {createdUser && <CopyableSecret secret={createdUser.temp_password} testId="created-temp-password" />}
          </div>
          <DialogFooter>
            <button type="button" onClick={() => setCreatedUser(null)} className="min-h-[44px] bg-[#1A1A18] text-[#FAF8F3] px-5 py-2 font-mono-label hover:bg-[#4A7A3D]" data-testid="created-close">
              J'ai noté le mot de passe
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reset password confirmation */}
      <Dialog open={!!resetTarget} onOpenChange={(o) => !o && !resetSubmitting && setResetTarget(null)}>
        <DialogContent data-testid="reset-password-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Réinitialiser le mot de passe ?</DialogTitle>
            <DialogDescription className="text-[#4B4B48] leading-relaxed pt-2">
              Un nouveau mot de passe temporaire sera généré pour <strong>{resetTarget?.name}</strong> (<code className="break-all">{resetTarget?.email}</code>). L'ancien mot de passe sera invalidé immédiatement et tout verrouillage anti-brute-force sera levé.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex flex-col sm:flex-row gap-2">
            <button type="button" onClick={() => setResetTarget(null)} disabled={resetSubmitting} className="min-h-[44px] border-2 border-[#1A1A18] px-5 py-2 font-mono-label hover:bg-[#F5EFEB] disabled:opacity-50">Annuler</button>
            <button type="button" onClick={submitResetPwd} disabled={resetSubmitting} className="min-h-[44px] bg-[#C4944A] text-[#1A1A18] px-5 py-2 font-mono-label hover:bg-[#1A1A18] hover:text-[#FAF8F3] disabled:opacity-50" data-testid="reset-confirm">
              {resetSubmitting ? "Génération…" : "Oui, réinitialiser"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reset password reveal */}
      <Dialog open={!!resetResult} onOpenChange={(o) => !o && setResetResult(null)}>
        <DialogContent data-testid="reset-result-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Nouveau mot de passe · transmets-le maintenant</DialogTitle>
            <DialogDescription className="text-[#4B4B48] leading-relaxed pt-2">
              Mot de passe temporaire pour <code className="break-all">{resetResult?.email}</code>. Ne sera plus jamais affiché.
            </DialogDescription>
          </DialogHeader>
          <div className="pt-2">
            {resetResult && <CopyableSecret secret={resetResult.temp_password} testId="reset-temp-password" />}
          </div>
          <DialogFooter>
            <button type="button" onClick={() => setResetResult(null)} className="min-h-[44px] bg-[#1A1A18] text-[#FAF8F3] px-5 py-2 font-mono-label hover:bg-[#4A7A3D]" data-testid="reset-result-close">
              J'ai noté
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit user dialog */}
      <Dialog open={!!editTarget} onOpenChange={(o) => !o && !editSubmitting && setEditTarget(null)}>
        <DialogContent data-testid="edit-user-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Modifier l'utilisateur</DialogTitle>
            <DialogDescription className="text-[#4B4B48] leading-relaxed pt-2">
              Modifier le nom, l'email ou le rôle. La modification est appliquée immédiatement et n'invalide pas le mot de passe.
            </DialogDescription>
          </DialogHeader>
          {editTarget && (
            <form onSubmit={submitEdit} className="space-y-4 pt-2">
              <div>
                <label htmlFor="edit-name" className="font-mono-label text-[#1A1A18] block mb-2">Nom complet</label>
                <input id="edit-name" type="text" required value={editTarget.name} onChange={(e) => setEditTarget({ ...editTarget, name: e.target.value })} className="w-full border-2 border-[#1A1A18] bg-[#FAF8F3] px-3 py-3 font-body text-base focus:outline-none focus:border-[#4A7A3D]" data-testid="edit-name" />
              </div>
              <div>
                <label htmlFor="edit-email" className="font-mono-label text-[#1A1A18] block mb-2">Email (identifiant de connexion)</label>
                <input id="edit-email" type="email" required value={editTarget.email} onChange={(e) => setEditTarget({ ...editTarget, email: e.target.value })} className="w-full border-2 border-[#1A1A18] bg-[#FAF8F3] px-3 py-3 font-body text-base focus:outline-none focus:border-[#4A7A3D]" data-testid="edit-email" />
              </div>
              <div>
                <label htmlFor="edit-role" className="font-mono-label text-[#1A1A18] block mb-2">Rôle</label>
                <select id="edit-role" value={editTarget.role} onChange={(e) => setEditTarget({ ...editTarget, role: e.target.value })} className="w-full border-2 border-[#1A1A18] bg-[#FAF8F3] px-3 py-3 font-body text-base focus:outline-none focus:border-[#4A7A3D]" data-testid="edit-role">
                  <option value="editor">Éditeur</option>
                  <option value="admin">Administrateur</option>
                </select>
              </div>
              {editError && <div className="border-l-4 border-red-700 bg-red-50 p-3 text-sm text-red-900" data-testid="edit-error">{editError}</div>}
              <DialogFooter className="flex flex-col sm:flex-row gap-2 pt-2">
                <button type="button" onClick={() => setEditTarget(null)} disabled={editSubmitting} className="min-h-[44px] border-2 border-[#1A1A18] px-5 py-2 font-mono-label hover:bg-[#F5EFEB] disabled:opacity-50">Annuler</button>
                <button type="submit" disabled={editSubmitting} className="min-h-[44px] bg-[#4A7A3D] text-[#FAF8F3] px-5 py-2 font-mono-label hover:bg-[#3E6633] disabled:opacity-50" data-testid="edit-submit">
                  {editSubmitting ? "Enregistrement…" : "Enregistrer"}
                </button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete user confirmation */}
      <Dialog open={!!deleteTarget} onOpenChange={(o) => !o && !deleteSubmitting && setDeleteTarget(null)}>
        <DialogContent data-testid="delete-user-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl text-red-700">Supprimer cet utilisateur ?</DialogTitle>
            <DialogDescription className="text-[#4B4B48] leading-relaxed pt-2">
              Le compte de <strong>{deleteTarget?.name}</strong> (<code className="break-all">{deleteTarget?.email}</code>) sera <strong className="text-red-700">définitivement supprimé</strong>. Cette action est irréversible.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex flex-col sm:flex-row gap-2">
            <button type="button" onClick={() => setDeleteTarget(null)} disabled={deleteSubmitting} className="min-h-[44px] border-2 border-[#1A1A18] px-5 py-2 font-mono-label hover:bg-[#F5EFEB] disabled:opacity-50">Annuler</button>
            <button type="button" onClick={submitDelete} disabled={deleteSubmitting} className="min-h-[44px] bg-red-700 text-[#FAF8F3] px-5 py-2 font-mono-label hover:bg-red-800 disabled:opacity-50" data-testid="delete-confirm">
              {deleteSubmitting ? "Suppression…" : "Oui, supprimer définitivement"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
